import { useEffect, useRef, useState } from "react";

/** Two taps closer than this are a double tap: the platform's own usual window. */
export const DOUBLE_TAP_MS = 300;
/** A finger that travels further than this was swiping, however it ended. */
const TAP_SLOP_PX = 10;
/** A finger held longer than this was resting on the glass, not tapping. */
const TAP_MAX_MS = 500;

/**
 * Hears ONE tap. Fires DOUBLE_TAP_MS after a tap with no second one behind it, because a
 * tap cannot know it was single any sooner. A swipe never fires (the browser takes the
 * gesture and cancels the pointer, or it ends far from where it began), and a double tap
 * is two taps that cancel each other out. Pure of React and the DOM's event types, so a
 * check can feed it a gesture and a fake clock.
 */
export function tapListener(onSingleTap: () => void) {
  let down: { x: number; y: number; at: number } | null = null;
  let waiting: ReturnType<typeof setTimeout> | undefined;
  return {
    down(x: number, y: number, at: number) {
      down = { x, y, at };
    },
    cancel() {
      down = null;
    },
    up(x: number, y: number, at: number) {
      const d = down;
      down = null;
      if (!d || Math.hypot(x - d.x, y - d.y) > TAP_SLOP_PX || at - d.at > TAP_MAX_MS) return;
      if (waiting !== undefined) {
        clearTimeout(waiting);
        waiting = undefined;
        return;
      }
      waiting = setTimeout(() => {
        waiting = undefined;
        onSingleTap();
      }, DOUBLE_TAP_MS);
    },
    dispose() {
      if (waiting !== undefined) clearTimeout(waiting);
    },
  };
}

const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown"]);

/**
 * useScreenIdle for a surface where touching the glass means something else (owner,
 * 2026-10-31). On an infinite scroll a swipe is the next page and a double tap is a fap;
 * neither asks for the chrome, so neither brings it back, and only a single tap does. A
 * paging key is the keyboard's swipe. A mouse has no such gestures and wakes it by moving,
 * as everywhere. Pointer events, not touch and mouse ones: a tap also fires emulated
 * mouse events, which would wake it on the first tap of a double.
 */
export function useTapIdle(idleMs: number): boolean {
  const [hidden, setHidden] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const arm = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setHidden(true), idleMs);
    };
    const wake = () => {
      setHidden((prev) => (prev ? false : prev));
      arm();
    };
    const tap = tapListener(wake);
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "mouse") wake();
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse") wake();
      else tap.down(e.clientX, e.clientY, e.timeStamp);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") tap.up(e.clientX, e.clientY, e.timeStamp);
    };
    const onCancel = () => tap.cancel();
    const onKey = (e: KeyboardEvent) => {
      if (!SCROLL_KEYS.has(e.key)) wake();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("pointercancel", onCancel, { passive: true });
    window.addEventListener("keydown", onKey);
    arm();

    return () => {
      if (timer.current) clearTimeout(timer.current);
      tap.dispose();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
    };
  }, [idleMs]);

  return hidden;
}
